// Copyright (c) StrangeBee 2023

import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { ActionListRendererParams, handleNotificationError, mergeTag, trimTags, useLocalMessage } from '@Helpers/';
import {
    ObservableOutput,
    ActionOutput,
    AnalyzerOutput,
    ObservableTypeOutput,
    AdminPermissions,
    OrgPermissions,
    SharedOrgOutput,
    PermissionsEnum,
    AuditOutput,
} from 'thehive-sdk';
import { extractData } from '../extractData';
import Details from '@Molecules/Details/Details';
import { ObservableEventList } from '@Organisms/Observable/eventList';
import { useCurrentUser } from '@Queries/userQueries';
import { useGenericQuery } from '@Queries/genericQueries';
import client from '@Client';
import { STREAM_EVENTS } from '@Services/StreamSrv';
import { useStreamObserver } from '@Hooks/useStream';
import { THForm } from '@Molecules/THForm/THForm';
import { Form, Space } from 'antd';
import AssigneeInfo from '@Molecules/AssigneeInfo/AssigneeInfo';
import { FormatDate } from '@Molecules/Format';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import { Link } from 'react-router-dom';
import RouteBuilder from '@Services/RouteBuilder';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { DisplayMetadata } from '@Molecules/DisplayMetadata/DisplayMetadata';
import useListQuery from '@Hooks/useListQuery';
import { CopyValue } from '@Atoms/CopyValue/CopyValue';
import { ActionsList } from '@Atoms/ActionsList/ActionsList';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { formatObservableTitle } from '@Helpers/observable';
import { ICONS } from '@Constants/iconsApp';
import { computeEventList as computeCaseEventList } from '@Organisms/Case/eventList';
import { useDrawer } from '@Hooks/useDrawer';
import { useSplitPermissions } from '@Hooks/useSplitPermissions';

import './ObservableDrawer.scss';

export interface ObservableDrawerProps {
    data: ObservableOutput;
    eventList: ObservableEventList;
    triggerElem?: ReactElement;
    caseId?: string;
    alertId?: string;
    actionList?: ActionListRendererParams<ObservableOutput>;
    defaultVisible?: boolean;
    onVisibleChange?: (visible: boolean) => void;
    visible?: boolean;
    permissions?: AdminPermissions[] | OrgPermissions[];
}

export const ObservableDrawer = ({
    data,
    eventList,
    triggerElem,
    caseId,
    alertId,
    actionList,
    defaultVisible = false,
    onVisibleChange,
    visible,
    permissions = [],
}: ObservableDrawerProps): ReactElement => {
    const i = useLocalMessage();

    const { dataType = '', _id: observableId, _createdAt, _createdBy, _updatedAt, tlp, pap } = data;
    const [form] = Form.useForm();

    const [fieldsTouched, setFieldsTouched] = useState(false);
    const { data: currentUser } = useCurrentUser();
    const { organisation = '', organisations = [] } = currentUser || {};
    const links = useMemo(
        () => organisations.find(({ organisation: orgName }) => orgName === organisation)?.links || [],
        [organisation, organisations],
    );

    const currentType = caseId ? 'case' : 'alert';
    const currentId = caseId ? caseId : alertId;

    const hasPermissions = useSplitPermissions(permissions, [
        PermissionsEnum.MANAGE_OBSERVABLE,
        PermissionsEnum.MANAGE_SHARE,
        PermissionsEnum.MANAGE_ANALYSE,
    ]);

    const [currentData, setCurrentData] = useState<ObservableOutput>(data);
    const [updatedData, setUpdatedData] = useState<boolean>(false);
    const [forceUpdate, setForceUpdate] = useState<boolean>(false);

    const [editMode, setEditMode] = React.useState(false);

    const { visible: visibleDrawer, setVisible: setVisibleDrawer } = useDrawer({});
    const [tmpRefetch, setTmpRefetch] = React.useState<string[]>([]);

    const { data: similarData = [], refetch: refetchSimilarData } = useListQuery<ObservableOutput[]>({
        queryParams: {
            selector: {
                head: [{ name: 'getObservable', idOrName: observableId }, { name: 'similar' }, { name: 'dedup' }],
                name: `get-similar-observable-${observableId}`,
                extraData: ['links'],
            },
            paginationOptions: {
                itemsPerPage: 100,
                page: 0,
            },
        },
        controller: client.ObservableController,
    });

    const { data: observableReportList, refetch: refetchObservableReportList } = useListQuery<ActionOutput[]>({
        queryParams: {
            selector: {
                name: 'get-all-observable-reports',
                head: [
                    {
                        name: 'getObservable',
                        idOrName: observableId,
                    },
                    {
                        name: 'actions',
                    },
                ],
            },
            sort: ['-startDate'],
        },
        controller: client.ObservableController,
    });

    const { data: observableTypeList = [], refetch: refetchObservableTypeList } = useListQuery<ObservableTypeOutput[]>({
        queryParams: {
            selector: {
                head: [{ name: 'listObservableType' }],
                name: 'get-all-observable-types',
            },
            sort: ['+name'],
        },
        controller: client.ObservableTypeController,
        manual: true,
        fetchOnStart: false,
    });

    const { data: analyzerData = [], refetch: refetchAnalyzer } = useGenericQuery<AnalyzerOutput[]>({
        fetchCallback: () => client.AnalyzerController.listAnalyzer(),
        key: ['get-all-analyzer'],
        manual: true,
        fetchOnStart: false,
    });

    const { data: analysisData = [], refetch: jobRefetch } = useListQuery<ActionOutput[]>({
        queryParams: {
            selector: {
                head: [{ name: 'getObservable', idOrName: observableId }, { name: 'jobs' }],
                name: `get-job-analyzer-${observableId}`,
            },
        },
        controller: client.ObservableController,
        manual: true,
        fetchOnStart: false,
    });

    const caseEventList = useMemo(
        () =>
            computeCaseEventList({
                controller: client.CaseController,
                locale: i,
                onError: handleNotificationError,
                onSuccess: () => {
                    return;
                },
            }),
        [i],
    );

    const { data: sharedOrgList = [], refetch: refetchSharedOrgList } = useGenericQuery<SharedOrgOutput[]>({
        fetchCallback: () => (caseId ? caseEventList.shares.onList(caseId) : Promise.resolve([] as SharedOrgOutput[])),
        key: ['sharedOrgList', caseId],
    });

    const { data: shareList = [], refetch: refetchShareList } = useGenericQuery({
        fetchCallback: () =>
            eventList.shares.onList(observableId, [
                {
                    field: 'organisationName',
                    type: 'string',
                    values: organisation,
                    operator: 'not',
                    describeAttribute: '' as any,
                },
            ]),
        key: ['sharings', observableId],
    });

    useStreamObserver({ objectType: STREAM_EVENTS.ANALYZER }, (audits) => {
        const isRightParent = audits.some(
            (action) =>
                (action.base.context?._type?.toUpperCase() === currentType?.toUpperCase() &&
                    action.base.context?._id === currentId) ||
                (action.base.context?.[currentType] && action.base.context?.[currentType]._id === currentId),
        );

        if (!isRightParent) {
            return false;
        }

        jobRefetch();
    });

    useStreamObserver({ rootId: caseId, objectType: STREAM_EVENTS.CASE }, (data) => {
        const canRefetchShare = data.some(({ base }) => {
            const { details } = base || {};
            return details?.share || details?.taskRule || details?.observableRule;
        });
        if (canRefetchShare) {
            refetchSharedOrgList();
        }
    });

    useStreamObserver(
        { rootId: caseId, objectType: STREAM_EVENTS.OBSERVABLE },
        (data) => {
            const canRefetch = data.some((action) => action.base.objectId === observableId);

            if (canRefetch) {
                if (!visibleDrawer && !editMode) {
                    refetchShareList();
                } else {
                    setTmpRefetch([...tmpRefetch, 'refetchShareList']);
                }
            }
        },
        [visibleDrawer, editMode],
    );

    useStreamObserver(
        { objectType: STREAM_EVENTS.ACTION },
        (audits: AuditOutput[]): void => {
            const actionIsObs = (audit: AuditOutput): boolean => audit.base?.details?.objectType === 'Observable';
            const canRefetch = audits.some(actionIsObs);

            if (canRefetch) {
                if (!visibleDrawer && !editMode) {
                    refetchObservableReportList();
                } else {
                    setTmpRefetch([...tmpRefetch, 'refetchObservableReportList']);
                }
            }
        },
        [visibleDrawer, editMode],
    );

    const parsedAnalyzerData = useMemo(
        () =>
            analyzerData
                .filter((analyzer) => analyzer.dataTypeList.includes(dataType))
                .sort((a, b) => a.name.localeCompare(b.name)),
        [analyzerData, dataType],
    );

    const observableTypeParsed = observableTypeList.find(({ name }) => name === dataType);

    const editObservable = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        ({ startDate, _createdAt, _updatedAt, data: dataSource, attachment, tags, ...values }) => {
            const treatedTag = tags ? mergeTag(currentData?.tags || [], data?.tags || [], tags) : [];

            eventList
                .onEdit(
                    {
                        ...values,
                        ...(tags ? { tags: trimTags(treatedTag) } : {}),
                    },
                    observableId,
                )
                .finally(() => {
                    setForceUpdate(true);
                });

            setFieldsTouched(false);
            setEditMode(false);
        },
        [data, observableId, currentData, eventList],
    );

    useEffect(() => {
        form.setFieldsValue(currentData);
        form.resetFields();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentData]);

    useEffect(() => {
        if (!editMode && updatedData) {
            setCurrentData(data);
            setUpdatedData(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editMode, updatedData]);

    useEffect(() => {
        if ((!editMode && data) || forceUpdate) {
            setCurrentData(data);
            setForceUpdate(false);
        } else {
            setUpdatedData(true);
        }
    }, [data, editMode, forceUpdate]); // eslint-disable-line

    useEffect(() => {
        if (!visibleDrawer && !editMode && tmpRefetch.length) {
            tmpRefetch.includes('refetchShareList') && refetchShareList();
            tmpRefetch.includes('refetchObservableReportList') && refetchObservableReportList();
            setTmpRefetch([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visibleDrawer, editMode, tmpRefetch]);

    const forbiddenFields = useMemo(() => {
        const fields = [];

        if (!links.length) {
            fields.push('#computeShares');
        }

        return fields;
    }, [links]);

    return (
        <THDrawer
            title={
                <Space>
                    <ICONS.ObservableIcon />
                    {i('observable.preview')}
                </Space>
            }
            triggerElem={triggerElem}
            className="drawer-form"
            width="50vw"
            disableClose={fieldsTouched}
            defaultVisible={defaultVisible}
            visible={visible}
            afterVisibleChange={(visible) => {
                if (visible) {
                    jobRefetch();
                    refetchAnalyzer();
                    refetchObservableTypeList();
                    refetchShareList();
                    refetchSimilarData();
                    refetchObservableReportList();
                }

                form.resetFields();
                onVisibleChange?.(visible);
            }}
        >
            <THForm<ObservableOutput>
                form={form}
                editable
                initialValues={currentData}
                onValuesChange={() => {
                    setFieldsTouched(true);
                    setEditMode(true);
                }}
                //eslint-disable-next-line
                onFinish={editObservable}
                submitTouchedValues
            >
                <THForm.Layout
                    header={
                        <DisplayMetadata
                            items={[
                                {
                                    icon: <ICONS.SettingIcon />,
                                    label: 'id',
                                    value: <CopyValue value={observableId}>{observableId}</CopyValue>,
                                },
                                {
                                    icon: <ICONS.UserIcon />,
                                    label: i('common.created-by'),
                                    value: <AssigneeInfo assignee={_createdBy} noAvatar />,
                                },
                                {
                                    icon: <ICONS.CreationDateIcon />,
                                    label: i('common.created-at'),
                                    value: <FormatDate date={_createdAt} />,
                                },
                                {
                                    icon: <ICONS.UpdateDateIcon />,
                                    label: i('common.updated-at'),
                                    value: <FormatDate date={_updatedAt} />,
                                    hidden: !_updatedAt,
                                },
                            ]}
                        />
                    }
                    footer={
                        editMode ? (
                            <FormSubmitButton
                                onCancel={() => {
                                    form.resetFields();
                                    setForceUpdate(true);
                                    setFieldsTouched(false);
                                    setEditMode(false);
                                }}
                                disabled={false}
                                footerText={i('general.confirm')}
                            />
                        ) : (
                            <THDrawer.Footer
                                actions={
                                    actionList && (
                                        <ActionsList
                                            actionList={actionList(
                                                [data],
                                                () => {
                                                    //empty
                                                },
                                                'details',
                                            )}
                                            scope="single"
                                        />
                                    )
                                }
                            >
                                {caseId && (
                                    <Link
                                        to={RouteBuilder.compute('cases.observable', {
                                            caseId,
                                            observableId,
                                        })}
                                    >
                                        <THButton type="primary" block>
                                            {i('common.goToDetails')}
                                        </THButton>
                                    </Link>
                                )}
                            </THDrawer.Footer>
                        )
                    }
                >
                    <Details
                        data={{
                            ...data,
                            '#computeSecurity': {
                                tlp,
                                pap,
                            },
                            '#computeAnalyzer': {
                                analyzerData: parsedAnalyzerData,
                                analysisData,
                                observable: data,
                            },
                            '#computeResponderReportList': observableReportList,
                            '#computeObservableLinkList': similarData,
                            '#computeShares': caseId && { shareList, setVisible: setVisibleDrawer },
                            '#computeDataType': {
                                observableTypeList: observableTypeList.filter(
                                    ({ isAttachment }) => observableTypeParsed?.isAttachment === isAttachment,
                                ),
                                refetchObservableTypeList,
                            },
                            '#computeTitle': formatObservableTitle({ data: data.data, attachment: data.attachment }),
                        }}
                        forbidden={forbiddenFields}
                        extract={extractData({
                            data,
                            eventList,
                            locale: i,
                            hasPermissions,
                            shareList,
                            sharedOrgList: sharedOrgList.filter(
                                ({ organisationName }) => currentUser?.organisation !== organisationName,
                            ),
                            organisation,
                            caseId,
                            formEditMode: editMode,
                        })}
                    />
                </THForm.Layout>
            </THForm>
        </THDrawer>
    );
};
